/*
 * Copyright 2022 Siemens
 */

import {Settings} from './settings';

export type FetchStatus<T = unknown, U extends boolean = true> =
  | {
      status: 'ok';
      code: 200 | 201 | 204;
      data: U extends true ? T | null : T;
      headers?: Response['headers'];
      url?: URL;
    }
  | {
      status: 'error';
      code: 200 | 201 | 204;
      error: Error;
      data: T | null;
      headers?: Response['headers'];
      url?: URL;
    }
  | {
      status: 'error';
      code: 400 | 401 | 403 | 404 | 409 | 413 | 500 | 501 | 502 | 503 | null;
      error: Error;
      headers?: Response['headers'];
      url?: URL;
    }
  | {
      status: 'error';
      code: 400 | 401 | 403 | 404 | 409 | 413 | 500 | 501 | 502 | 503 | null;
      data: unknown | null;
      headers?: Response['headers'];
      url?: URL;
    };

export class Communication {
  static #baseAPI: string | undefined;
  static #baseWebsocket: string | undefined;

  static get baseAPI(): string {
    if (this.#baseAPI === undefined) {
      console.debug('Requesting for API before configured, returning hardcoded route');
      const {protocol, host, pathname} = window.location;
      return `${protocol}//${host}${pathname}orchestrator/`;
    }
    return this.#baseAPI;
  }

  static websocketAPI(workspace: string): string {
    if (this.#baseWebsocket === undefined) {
      console.debug('Requesting for WebSocket API before configured, returning hardcoded route');
      const {protocol, host, pathname} = window.location;
      return `${protocol.toLowerCase() === 'https:' ? 'wss:' : 'ws:'}//${host}${pathname}orchestrator/workspaces/${workspace}`;
    }
    return `${this.#baseWebsocket}${workspace}`;
  }

  static init(api: string): void {
    this.#baseAPI = api.endsWith('/') ? api : `${api}/`;
    const {protocol, host, pathname} = new URL(this.baseAPI);
    this.#baseWebsocket = `${protocol.toLowerCase() === 'https:' ? 'wss:' : 'ws:'}//${host}${pathname}workspaces/`;
  }

  static resolveApi(
    api:
      | string
      | {isWorkspaces: true}
      | {workspace: string | true; isUsers?: true}
      | {workspace: string | true; isDatastores?: true}
      | {workspace: string | true; datastore: string; isResources?: true}
      | {workspace: string | true; datastore: string; resource: string; isFiles?: true}
      | {workspace: string | true; datastore: string; resource: string; file: string},
  ): string {
    if (typeof api === 'object') {
      const parts = api;
      if ('isWorkspaces' in parts) api = 'workspaces';
      else {
        if (typeof parts.workspace === 'string') api = `workspaces/${parts.workspace}`;
        else if (typeof Settings.currentWorkspace.value.id === 'string' && Settings.currentWorkspace.value.id.length > 0)
          api = `workspaces/${Settings.currentWorkspace.value.id}`;
        else if (typeof Settings.currentWorkspace.value.name as unknown === 'string' && Settings.currentWorkspace.value.name.length > 0)
          api = `workspaces/${Settings.currentWorkspace.value.name}`;
        else api = 'workspaces/default';

        if ('isUsers' in parts) api = `${api}/users`;
        else if ('isDatastores' in parts) api = `${api}/datastores`;
        else if ('datastore' in parts) {
          api = `${api}/datastores/${parts.datastore}`;

          if ('isResources' in parts) api = `${api}/resources`;
          else if ('resource' in parts) {
            api = `${api}/resources/${parts.resource}`;

            if ('isFiles' in parts) api = `${api}/data`;
            else if ('file' in parts) api = `${api}/data/${parts.file}`;
          }
        }
      }
    }
    return api.indexOf('://') === -1 ? `${this.baseAPI}${api}` : api;
  }

  static async fetch<T = unknown, U extends boolean = true>(
    api:
      | string
      | {isWorkspaces: true}
      | {workspace: string | true; isUsers?: true}
      | {workspace: string | true; isDatastores?: true}
      | {workspace: string | true; datastore: string; isResources?: true}
      | {workspace: string | true; datastore: string; resource: string; isFiles?: true}
      | {workspace: string | true; datastore: string; resource: string; file: string},
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    returnType: 'json' | 'json?object' | 'json+object' | 'json+array' | 'stream' | ((result: Response) => Promise<unknown>),
    body?: undefined | string | Record<string, unknown> | FormData | ReadableStream<unknown>,
    query?: Record<string, boolean | string | number | null>,
    options: NonNullable<Parameters<typeof fetch>[1]> = {},
  ): Promise<FetchStatus<T, U>> {
    api = this.resolveApi(api);
    if (query !== undefined) {
      api = `${api}?${Object.keys(query)
        .map((key) => (query[key] === null ? key : `${key}=${encodeURI(`${query[key]}`)}`))
        .join('&')}`;
    }
    console.info('Communication', method, api);
    return this.#fetch<T, U>(api, method, body, options, returnType);
  }

  static #setFetchHeader(options: NonNullable<Parameters<typeof fetch>[1]>, key: string, value: string): void {
    if (options.headers === undefined) options.headers = {};

    if (options.headers instanceof Headers) options.headers.set(key, value);
    else if (Array.isArray(options.headers)) options.headers.push([key, value]);
    else options.headers[key] = value;
  }

  static async #fetch<T = unknown, U extends boolean = true>(
    url: string | URL,
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body?: string | Record<string, unknown> | FormData | ReadableStream,
    options?: NonNullable<Parameters<typeof fetch>[1]>,
    returnType?:
      | 'json'
      | 'json?object'
      | 'json+object'
      | 'json+array'
      | 'text'
      | 'form'
      | 'stream'
      | ((result: Response) => Promise<unknown>),
  ): Promise<FetchStatus<T, U>> {
    options = {
      method: method === undefined ? 'GET' : method,
      ...(options === undefined ? {} : options),
    };
    if (body !== undefined) {
      if (typeof body === 'string') {
        options.body = body;
        this.#setFetchHeader(options, 'content-type', 'text/plain');
      } else if (body instanceof FormData || body instanceof ReadableStream) {
        options.body = body;
      } else {
        options.body = JSON.stringify(body);
        this.#setFetchHeader(options, 'content-type', 'application/json');
      }
    }
    switch (returnType) {
      case 'stream':
        this.#setFetchHeader(options, 'accept', 'application/octet-stream');
        break;
      case 'json':
      case 'json?object':
      case 'json+object':
      case 'json+array':
        this.#setFetchHeader(options, 'accept', 'application/json');
        break;
      default:
        break;
    }
    if (typeof url === 'string')
      try {
        url = new URL(url);
      } catch (e) {
        return {status: 'error', code: null, error: e instanceof Error ? e : new Error(`${e}`)};
      }

    const result = await fetch(url, options).catch((cause) => new Error('Communication request failure', {cause}));
    if (result instanceof Error) return {status: 'error', code: null, error: result, url};

    let parsed: unknown;
    try {
      switch (returnType) {
        case 'json':
        case 'json?object':
        case 'json+object':
        case 'json+array':
          parsed = await result.json();
          break;
        case 'text':
          parsed = await result.text();
          break;
        case 'form':
          parsed = await result.formData();
          break;
        case 'stream':
          parsed = await result.blob();
          break;
        case undefined: {
          const contentType = (result.headers.get('content-type') ?? '').toLowerCase();
          if (contentType.includes('application/json')) {
            returnType = 'json';
            parsed = await result.json();
          } else if (contentType.includes('application/zip') || contentType.includes('application/octet-stream')) {
            returnType = 'stream';
            parsed = await result.blob();
          } else {
            returnType = 'text';
            parsed = await result.text();
          }
          break;
        }
        default:
          parsed = await returnType(result);
          break;
      }
    } catch (err) {
      console.error(
        'Parsing of response from',
        url,
        'with type',
        typeof returnType === 'string' ? returnType : '?',
        'failed\n',
        err,
      );
    }

    switch (result.status) {
      case 500:
      case 501:
      case 502:
      case 503:
        return {
          status: 'error',
          code: result.status,
          error: new Error(
            parsed !== null && typeof parsed === 'object' && typeof (parsed as {message: unknown}).message === 'string'
              ? (parsed as {message: string}).message
              : 'Unknown server error during logout action',
          ),
          headers: result.headers,
          url,
        };
      case 200:
      case 201:
      case 204:
      case 304:
        switch (returnType) {
          case 'json?object':
          case 'json+object':
          case 'json+array':
            const returnStatus =
              returnType === 'json?object'
                ? typeof parsed === 'object' && !Array.isArray(parsed)
                : returnType === 'json+object'
                ? parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
                : returnType === 'json+array'
                ? Array.isArray(parsed)
                : false;
            return returnStatus
              ? {
                  status: 'ok',
                  code: result.status === 304 ? 200 : result.status,
                  data: parsed as T,
                  headers: result.headers,
                  url,
                }
              : {
                  status: 'error',
                  code: result.status === 304 ? 200 : result.status,
                  error: new Error('Invalid data response'),
                  data: parsed as T,
                  headers: result.headers,
                  url,
                };
          default:
            return {
              status: 'ok',
              code: result.status === 304 ? 200 : result.status,
              data:
                parsed !== null &&
                typeof parsed === 'object' &&
                (parsed as {status: string}).status === 'ok' &&
                typeof (parsed as {message: unknown}).message === 'string'
                  ? (parsed as {message: T}).message
                  : (parsed as T),
              headers: result.headers,
              url,
            };
        }
      case 302: // Redirect is Ok
        return {
          status: 'ok',
          code: 200,
          data:
            parsed !== null &&
            typeof parsed === 'object' &&
            (parsed as {status: string}).status === 'ok' &&
            typeof (parsed as {message: unknown}).message === 'string'
              ? (parsed as {message: T}).message
              : (parsed as T),
          headers: result.headers,
          url,
        };
      case 400:
      case 401:
      case 403:
      case 404:
      case 409:
      case 413:
        return {
          status: 'error',
          code: result.status,
          data:
            parsed !== null && typeof parsed === 'object' && typeof (parsed as {message: unknown}).message === 'string'
              ? (parsed as {message: T}).message
              : (parsed as T),
          headers: result.headers,
          url,
        };
      default:
        return {
          status: 'error',
          code: null,
          error: new Error('Unknown data request response'),
          headers: result.headers,
          url,
        };
    }
  }
}
